// client/src/pages/auth/Register.js
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import authService from '../../services/authService.js';
import api from '../../services/api.js';
import Input from '../../components/ui/Input.js';
import Button from '../../components/ui/Button.js';
import ProgressSteps from '../../components/ui/ProgressSteps.js';

// ─── Sweepstakes ──────────────────────────────────────────────
// Set to false when no sweepstakes is running. Hides the optional
// eligibility checkbox and stops sending the field to the server.
// Leave the /rules page and route in place regardless — old links to the
// Official Rules should keep resolving after a promotion ends.
const SWEEPSTAKES_ENABLED = true;

// If user came from pricing, show "Confirm Plan" instead of "Choose Plan"
function getSteps(hasPendingPlan) {
  return [
    { id: 'account', label: 'Create Account' },
    { id: 'choose-plan', label: hasPendingPlan ? 'Confirm Plan' : 'Choose Plan' },
    { id: 'payment', label: 'Payment' },
  ];
}

export default function Register() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: email, 2: verify code, 3: complete
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [form, setForm] = useState({ password: '', confirmPassword: '', firstname: '', lastname: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Optional — never blocks registration.
  const [sweepstakesEligible, setSweepstakesEligible] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if user came from pricing page with a pre-selected plan
  const pendingPlan = (() => {
    try {
      const stored = sessionStorage.getItem('pendingPlan');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  // Check if user came from pricing page wanting a free trial
  const pendingTrial = sessionStorage.getItem('pendingTrial');

  // Check for ?redirect= query param. Used by flows that send a logged-out
  // user to register first (e.g. gift redemption: /register?redirect=/gift/redeem/XXX).
  // After registration completes, send them back where they were headed.
  // Only honored for same-origin paths (must start with `/`) so an attacker
  // can't craft a redirect to a phishing site by passing a full URL.
  const redirectParam = searchParams.get('redirect');
  const safeRedirect = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;

  const steps = getSteps(!!pendingPlan);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.register(email);
      setStep(2);
    } catch (err) {
      setError(err.data?.error || 'Failed to send code');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.verify(email, code);
      setStep(3);
    } catch (err) {
      setError(err.data?.error || 'Invalid code');
    }
    setLoading(false);
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to create an account');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await authService.complete({
        email, code, password: form.password,
        firstname: form.firstname, lastname: form.lastname,
        agreedToTerms: true,
        // Only sent while a sweepstakes is running. The server records a
        // timestamp when true and leaves the column NULL otherwise.
        ...(SWEEPSTAKES_ENABLED && { sweepstakesEligibility: sweepstakesEligible }),
      });
      await loginWithToken(data.token);

      // Priority 1: User clicked "Start Free Trial" on pricing page
      if (pendingTrial) {
        const tierId = parseInt(pendingTrial);
        sessionStorage.removeItem('pendingTrial');
        try {
          await api.post('/auth/activate-trial', { tierId });
        } catch (trialErr) {
          // Trial may have already been activated by auto-trial on registration
          // or the tier no longer has trial enabled — either way, continue
          console.log('Trial activation after registration:', trialErr.data?.error || 'skipped');
        }
        // Reload to pick up the updated user data with trial
        window.location.href = '/';
        return;
      }

      // Priority 2: User had a plan selected from pricing, go straight to checkout
      if (pendingPlan) {
        navigate(`/checkout?tier=${pendingPlan.tier}&interval=${pendingPlan.interval}`);
        return;
      }

      // Priority 3: User came in via a redirect link (e.g. gift redemption).
      // Send them back to where they were headed before being asked to register.
      if (safeRedirect) {
        navigate(safeRedirect);
        return;
      }

      // Default: show plan selection
      navigate('/choose-plan');
    } catch (err) {
      setError(err.data?.error || 'Registration failed');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    try {
      await authService.resendCode(email);
      setError('');
    } catch (err) {
      setError(err.data?.error || 'Failed to resend');
    }
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-md mx-auto px-4 py-12">
        <ProgressSteps steps={steps} currentStep="account" />

        <h1 className="font-display text-3xl text-gold-dark dark:text-gold text-center mb-2">Create Account</h1>

        {/* Show selected plan badge if coming from pricing */}
        {pendingPlan && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              Selected: {pendingPlan.tierName} — {pendingPlan.interval}
            </span>
          </div>
        )}

        {/* Show trial badge if coming from free trial button */}
        {pendingTrial && !pendingPlan && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              ✨ Free trial — no credit card required
            </span>
          </div>
        )}

        {/* Show gift badge if registering via a gift redemption link.
            Only highlights gift redemption specifically — other redirect
            destinations don't get a badge since we don't know what they
            represent. */}
        {!pendingPlan && !pendingTrial && safeRedirect && safeRedirect.startsWith('/gift/redeem/') && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              🎁 Create your account to claim your gift
            </span>
          </div>
        )}

        <p className="text-royal-500 dark:text-royal-200 text-center mb-6">Step {step} of 3</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-600 dark:text-red-300 text-sm">{error}</div>
        )}

        <div className="bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl p-6">
          {step === 1 && (
            <form onSubmit={handleEmailSubmit}>
              <Input label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending code...' : 'Send Verification Code'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerify}>
              <p className="text-royal-100 mb-3 text-sm">
                We've sent a 6-digit verification code to <strong className="text-white">{email}</strong> to finish creating your account.
              </p>
              <div className="bg-royal-800/60 border border-royal-600/50 rounded-lg p-3 mb-4">
                <p className="text-royal-200 text-xs leading-relaxed">
                  <span className="text-gold font-semibold">Don't see it?</span> Check your spam or junk folder — verification emails can sometimes end up there. If you still don't see it, tap "Resend code" below.
                </p>
              </div>
              <Input
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                placeholder="000000"
                className="text-center text-2xl tracking-widest"
                required
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <button type="button" onClick={handleResend} className="block w-full text-center mt-3 text-royal-100 hover:text-gold text-sm">
                Resend code
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleComplete}>
              <div className="grid grid-cols-2 gap-4">
                <Input label="First Name" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} required />
                <Input label="Last Name" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} required />
              </div>
              <Input label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              <Input label="Confirm Password" type="password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required />

              {/* Terms and Privacy Agreement */}
              <label className="flex items-start gap-3 mt-4 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50 flex-shrink-0"
                />
                <span className="text-royal-100 text-sm leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-gold hover:text-gold-light underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" target="_blank" className="text-gold hover:text-gold-light underline">
                    Privacy Policy
                  </Link>
                </span>
              </label>

              {/* Sweepstakes eligibility — OPTIONAL.
                  Deliberately does not gate the submit button. Leaving it
                  unchecked creates a normal account; it only means the user
                  isn't confirming eligibility for a sweepstakes offer.
                  Set SWEEPSTAKES_ENABLED to false at the top of this file to
                  hide the whole block. */}
              {SWEEPSTAKES_ENABLED && (
                <div className="bg-royal-800/60 border border-royal-600/50 rounded-lg p-3 mb-5">
                  <p className="text-royal-200 text-xs mb-2">
                    <span className="text-gold font-semibold">Optional</span> — for sweepstakes offers only
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sweepstakesEligible}
                      onChange={(e) => setSweepstakesEligible(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50 flex-shrink-0"
                    />
                    <span className="text-royal-100 text-sm leading-relaxed">
                      I am 18 or older and a legal resident of the United States, and I agree to the{' '}
                      <Link to="/rules" target="_blank" className="text-gold hover:text-gold-light underline">
                        Official Rules
                      </Link>
                    </span>
                  </label>
                  <p className="text-royal-200 text-xs leading-relaxed mt-2">
                    You can create your account either way. No purchase necessary.
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading || !agreedToTerms}>
                {loading ? 'Creating account...' : 'Complete Registration'}
              </Button>
            </form>
          )}
        </div>

        {/* Login link — preserve the redirect so users who click "Log In"
            don't lose their place in the gift redemption flow. */}
        <p className="text-center mt-6 text-royal-500 dark:text-royal-200 text-sm">
          Already have an account?{' '}
          <Link
            to={safeRedirect ? `/login?redirect=${encodeURIComponent(safeRedirect)}` : '/login'}
            className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light"
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}